import { useEffect, useRef } from "react";
import type { V2Scene, ViewportProfile } from "../types";

/* ============================================================
   V4 · Living Water（活水时刻）
   —— 一艘"你的纸船"随三问推进连续航行（离岸 → 湖心 → 穿雾），
      输入与选择都会在它身边的水面泛起涟漪，
      行过的路留下渐渐淡去的金色水痕（canvas 尾迹），
      终幕纸船化作灯火升腾。

   设计约束：
   - 船是"背景演员"（z 低于文字层），永远不遮挡正文；
   - Canvas 只负责涟漪 / 尾迹 / 灯火，全部轻量绘制；
   - prefers-reduced-motion：直接跳位，不播动画不画粒子；
   - 移动端自动缩小船只与粒子规模。
   ============================================================ */

type Pose = { l: number; b: number; s: number; o: number };

/** 桌面基准位：l=船心横坐标(%)，b=船底离地(%)，s=比例，o=不透明度。 */
const BASE_POSE: Record<V2Scene, Pose> = {
  intro:      { l: 6,  b: 15, s: .5,  o: 0 },
  question:   { l: 8,  b: 15, s: .55, o: 1 },
  first:      { l: 24, b: 15, s: .62, o: 1 },
  struggle:   { l: 42, b: 16, s: .72, o: 1 },
  dedication: { l: 60, b: 17, s: .82, o: 1 },
  reflection: { l: 68, b: 17, s: .88, o: 1 },
  theory:     { l: 47, b: 4,  s: .62, o: .92 },
  jiaxing:    { l: 14, b: 30, s: .8,  o: 1 },
  action:     { l: 77, b: 25, s: 1,   o: 1 },
  ticket:     { l: 11, b: 7,  s: .7,  o: 0 },
  lights:     { l: 77, b: 25, s: 1,   o: 0 },
  end:        { l: 77, b: 25, s: 1,   o: 0 },
};

/** 移动端覆盖：竖屏留白更少，船让位给正文与控件。 */
const MOBILE_POSE: Partial<Record<V2Scene, Pose>> = {
  question:   { l: 9,  b: 10, s: .5,  o: 1 },
  reflection: { l: 12, b: 24, s: .5,  o: .62 },
  theory:     { l: 7,  b: 4,  s: .48, o: .8 },
  jiaxing:    { l: 68, b: 3,  s: .48, o: .38 },
  action:     { l: 50, b: 6,  s: .58, o: 1 },
  ticket:     { l: 8,  b: 7,  s: .48, o: .85 },
};

function poseFor(scene: V2Scene, profile: ViewportProfile): Pose {
  return (profile === "mobile" ? MOBILE_POSE[scene] : undefined) ?? BASE_POSE[scene];
}

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

/** 二次贝塞尔上的点。 */
function quad(p0: { x: number; y: number }, c: { x: number; y: number }, p1: { x: number; y: number }, t: number) {
  const u = 1 - t;
  return {
    x: u * u * p0.x + 2 * u * t * c.x + t * t * p1.x,
    y: u * u * p0.y + 2 * u * t * c.y + t * t * p1.y,
  };
}

/** 二次贝塞尔导数（用于船身朝向）。 */
function quadDeriv(p0: { x: number; y: number }, c: { x: number; y: number }, p1: { x: number; y: number }, t: number) {
  const u = 1 - t;
  return {
    dx: 2 * (u * (c.x - p0.x) + t * (p1.x - c.x)),
    dy: 2 * (u * (c.y - p0.y) + t * (p1.y - c.y)),
  };
}

/**
 * S 形航道：先向右下绕（像避开一湾浅滩），再回正驶向目标。
 * 两段二次贝塞尔在中点平滑拼接，控制点对称偏离中线 lift 像素。
 */
function arcPoint(p0: { x: number; y: number }, p1: { x: number; y: number }, lift: number, t: number) {
  const m = { x: (p0.x + p1.x) / 2, y: (p0.y + p1.y) / 2 };
  const c1 = { x: m.x, y: m.y + lift };
  const c2 = { x: m.x, y: m.y - lift };
  return t < 0.5 ? quad(p0, c1, m, t * 2) : quad(m, c2, p1, (t - 0.5) * 2);
}

function arcAngle(p0: { x: number; y: number }, p1: { x: number; y: number }, lift: number, t: number) {
  const m = { x: (p0.x + p1.x) / 2, y: (p0.y + p1.y) / 2 };
  const c1 = { x: m.x, y: m.y + lift };
  const c2 = { x: m.x, y: m.y - lift };
  const d = t < 0.5 ? quadDeriv(p0, c1, m, t * 2) : quadDeriv(m, c2, p1, (t - 0.5) * 2);
  const deg = (Math.atan2(d.dy, d.dx) * 180) / Math.PI;
  return Math.max(-16, Math.min(16, deg * 0.9));
}

type Ripple = { x: number; y: number; r0: number; r1: number; born: number; dur: number; a0: number };
type Spark = { x: number; y: number; vx: number; vy: number; born: number; dur: number; size: number };
type TrailPoint = { x: number; y: number; t: number };

const RIPPLE_LIMIT = 42;
const SPARK_LIMIT = 70;

export function LivingWater({ scene, profile, pulse }: { scene: V2Scene; profile: ViewportProfile; pulse: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const boatRef = useRef<HTMLDivElement>(null);
  const reduce = useRef(false);
  const sizeRef = useRef({ w: 0, h: 0, dpr: 1, shipW: 120 });
  const cur = useRef({ x: 0, y: 0, s: 1, o: 0, rot: 0 });
  const tween = useRef<{ from: { x: number; y: number; s: number; o: number }; to: { x: number; y: number; s: number; o: number }; lift: number; t0: number } | null>(null);
  const rafId = useRef(0);
  const ripples = useRef<Ripple[]>([]);
  const sparks = useRef<Spark[]>([]);
  const trail = useRef<TrailPoint[]>([]);
  const spawnTimer = useRef(0);

  /* ---------- 几何换算 ---------- */
  function measure() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const w = window.innerWidth;
    const h = window.innerHeight;
    const dpr = Math.min(window.devicePixelRatio || 1, profile === "mobile" ? 1.5 : 2);
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    const shipW = Math.max(64, Math.min(w * (profile === "mobile" ? 0.21 : 0.125), 224));
    sizeRef.current = { w, h, dpr, shipW };
  }

  function applyPose(x: number, y: number, s: number, o: number, rot = 0) {
    const boat = boatRef.current;
    if (!boat) return;
    const { shipW } = sizeRef.current;
    const shipH = shipW * (120 / 210);
    boat.style.opacity = String(Math.max(0, Math.min(1, o)));
    if (o > 0.004) {
      boat.style.transform = `translate(${x - shipW / 2}px, ${y - shipH}px) scale(${s}) rotate(${rot.toFixed(2)}deg)`;
      boat.style.display = "block";
    } else {
      boat.style.display = "none";
    }
  }

  function anchorXY(): { x: number; y: number } {
    return { x: cur.current.x, y: cur.current.y };
  }

  /* ---------- 涟漪 / 灯火 ---------- */
  function spawnRipple(x: number, y: number, r0: number, r1: number, dur: number, a0: number) {
    if (reduce.current) return;
    const list = ripples.current;
    if (list.length >= RIPPLE_LIMIT) list.shift();
    list.push({ x, y, r0, r1, born: performance.now(), dur, a0 });
  }

  function rippleAtBoat(rounds: number, big: boolean) {
    const { x, y } = anchorXY();
    for (let i = 0; i < rounds; i += 1) {
      const jx = (Math.random() - 0.5) * (big ? 26 : 10);
      const jy = (Math.random() - 0.5) * 5;
      spawnRipple(x + jx, y + jy, big ? 12 : 5, big ? 118 : 34, big ? 1.8 : 1.05, big ? 0.5 : 0.34);
    }
  }

  function burstFromBoat() {
    if (reduce.current) return;
    const { x, y } = anchorXY();
    const list = sparks.current;
    for (let i = 0; i < 30; i += 1) {
      if (list.length >= SPARK_LIMIT) list.shift();
      const angle = Math.random() * Math.PI * 2;
      const speed = 14 + Math.random() * 46;
      list.push({
        x: x + (Math.random() - 0.5) * 60,
        y: y - Math.random() * 20,
        vx: Math.cos(angle) * speed * 0.45,
        vy: -(24 + Math.random() * 66),
        born: performance.now(),
        dur: 2.4 + Math.random() * 1.6,
        size: 1.2 + Math.random() * 2.1,
      });
    }
  }

  /* ---------- 绘制 ---------- */
  function draw(now: number) {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const { w, h, dpr } = sizeRef.current;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    /* 尾迹：船移动时记录路径点，分段按新旧淡出（双描边，深浅底都可见） */
    if (tween.current !== null && !reduce.current && cur.current.o > 0.08) {
      const { x, y } = anchorXY();
      const list = trail.current;
      const last = list[list.length - 1];
      if (!last || Math.hypot(last.x - x, last.y - y) > 1.2) {
        list.push({ x, y, t: now });
        if (list.length > 260) list.shift();
      }
    }
    const trailList = trail.current.filter((p) => now - p.t < 3000);
    trail.current = trailList;
    if (trailList.length > 1) {
      ctx.lineCap = "round";
      for (let i = 1; i < trailList.length; i += 1) {
        const age = (now - trailList[i].t) / 3000;
        const alpha = Math.max(0, 1 - age);
        ctx.beginPath();
        ctx.moveTo(trailList[i - 1].x, trailList[i - 1].y);
        ctx.lineTo(trailList[i].x, trailList[i].y);
        ctx.strokeStyle = `rgba(23,42,46,${0.1 * alpha})`;
        ctx.lineWidth = 2.6;
        ctx.stroke();
        ctx.strokeStyle = `rgba(237,201,131,${0.34 * alpha})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }
    }

    /* 涟漪 */
    const rl = ripples.current;
    for (let i = rl.length - 1; i >= 0; i -= 1) {
      const r = rl[i];
      const t = (now - r.born) / (r.dur * 1000);
      if (t >= 1) { rl.splice(i, 1); continue; }
      const k = easeOutCubic(t);
      const radius = r.r0 + (r.r1 - r.r0) * k;
      ctx.beginPath();
      ctx.arc(r.x, r.y, radius, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(255,236,190,${(r.a0 * (1 - t)).toFixed(3)})`;
      ctx.lineWidth = 1.1;
      ctx.stroke();
      if (r.r1 > 60) {
        ctx.beginPath();
        ctx.arc(r.x, r.y, radius * 0.66, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(255,236,190,${(r.a0 * 0.42 * (1 - t)).toFixed(3)})`;
        ctx.lineWidth = 0.8;
        ctx.stroke();
      }
    }

    /* 灯火（终幕） */
    const sp = sparks.current;
    for (let i = sp.length - 1; i >= 0; i -= 1) {
      const p = sp[i];
      const t = (now - p.born) / (p.dur * 1000);
      if (t >= 1) { sp.splice(i, 1); continue; }
      const px = p.x + p.vx * t;
      const py = p.y + p.vy * t;
      const alpha = Math.sin(Math.min(1, t) * Math.PI);
      ctx.beginPath();
      ctx.arc(px, py, p.size * 2.6, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(255,214,140,${(alpha * 0.22).toFixed(3)})`;
      ctx.fill();
      ctx.beginPath();
      ctx.arc(px, py, p.size, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(255,236,196,${alpha.toFixed(3)})`;
      ctx.fill();
    }
  }

  function frame(now: number) {
    const tw = tween.current;
    if (tw) {
      const raw = Math.min(1, (now - tw.t0) / 3000);
      const t = easeOutCubic(raw);
      const oT = Math.min(1, (now - tw.t0) / 1800);
      const oK = easeOutCubic(oT);
      const p = arcPoint(tw.from, tw.to, tw.lift, t);
      const rot = arcAngle(tw.from, tw.to, tw.lift, t);
      const ns = tw.from.s + (tw.to.s - tw.from.s) * t;
      const no = tw.from.o + (tw.to.o - tw.from.o) * oK;
      cur.current = { x: p.x, y: p.y, s: ns, o: no, rot };
      if (raw >= 1 && oT >= 1) {
        cur.current = { ...cur.current, x: tw.to.x, y: tw.to.y, rot: 0 };
        tween.current = null;
      }
    }
    applyPose(cur.current.x, cur.current.y, cur.current.s, cur.current.o, cur.current.rot);
    draw(now);
    if (tween.current !== null || ripples.current.length || sparks.current.length || trail.current.length) {
      rafId.current = window.requestAnimationFrame(frame);
    } else {
      rafId.current = 0;
    }
  }

  function startLoop() {
    if (!rafId.current) rafId.current = window.requestAnimationFrame(frame);
  }

  function setTarget(sceneName: V2Scene, immediate: boolean) {
    const pose = poseFor(sceneName, profile);
    const { w, h } = sizeRef.current;
    const tx = (pose.l / 100) * w;
    const ty = h - (pose.b / 100) * h;
    if (immediate || reduce.current) {
      cur.current = { x: tx, y: ty, s: pose.s, o: pose.o, rot: 0 };
      tween.current = null;
      applyPose(tx, ty, pose.s, pose.o);
      return;
    }
    const dist = Math.hypot(tx - cur.current.x, ty - cur.current.y);
    if (dist < 4) {
      cur.current = { x: tx, y: ty, s: pose.s, o: pose.o, rot: 0 };
      tween.current = null;
      applyPose(tx, ty, pose.s, pose.o);
      return;
    }
    tween.current = {
      from: { x: cur.current.x, y: cur.current.y, s: cur.current.s, o: cur.current.o },
      to: { x: tx, y: ty, s: pose.s, o: pose.o },
      /* S 弧幅度：路越长弯越大，但不夸张（像绕开一处浅滩） */
      lift: Math.max(18, Math.min(dist * 0.16, 110)),
      t0: performance.now(),
    };
    startLoop();
  }

  /* ---------- 事件接线 ---------- */
  useEffect(() => {
    reduce.current = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    measure();
    const pose = poseFor(scene, profile);
    const { w, h } = sizeRef.current;
    const x = (pose.l / 100) * w;
    const y = h - (pose.b / 100) * h;
    cur.current = { x, y, s: pose.s, o: pose.o, rot: 0 };
    applyPose(x, y, pose.s, pose.o);

    const onResize = () => {
      measure();
      const p = poseFor(scene, profile);
      const { w: nw, h: nh } = sizeRef.current;
      cur.current = { x: (p.l / 100) * nw, y: nh - (p.b / 100) * nh, s: p.s, o: p.o, rot: 0 };
      applyPose(cur.current.x, cur.current.y, cur.current.s, cur.current.o);
      startLoop();
    };

    const onTyping = (event: Event) => {
      const detail = (event as CustomEvent<{ n?: number }>).detail;
      rippleAtBoat(detail?.n ?? 1, false);
      startLoop();
    };

    window.addEventListener("resize", onResize, { passive: true });
    window.addEventListener("v2:boat-ripple", onTyping as EventListener);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("v2:boat-ripple", onTyping as EventListener);
      if (rafId.current) window.cancelAnimationFrame(rafId.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* 场景变化：驶向新位，行过的水面泛圈微澜 */
  useEffect(() => {
    setTarget(scene, false);
    const timer = window.setTimeout(() => {
      if (!reduce.current && poseFor(scene, profile).o > 0.05) {
        rippleAtBoat(2, true);
        startLoop();
      }
    }, 1400);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene, profile]);

  /* 灯火幕：纸船化光 */
  useEffect(() => {
    if (scene !== "lights" || reduce.current) return;
    const timer = window.setTimeout(() => {
      burstFromBoat();
      startLoop();
    }, 900);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene]);

  /* 提交 / 选择：pulse 期间在船边荡开波纹 */
  useEffect(() => {
    if (!pulse || reduce.current) return;
    if (spawnTimer.current) window.clearTimeout(spawnTimer.current);
    spawnTimer.current = window.setTimeout(() => {
      if (poseFor(scene, profile).o > 0.05) {
        rippleAtBoat(3, true);
        startLoop();
      }
    }, 240);
    return () => {
      if (spawnTimer.current) window.clearTimeout(spawnTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pulse]);

  return (
    <div className="v2-living-water" aria-hidden="true">
      <canvas ref={canvasRef} />
      <div className="v2-boat-actor" ref={boatRef}>
        <svg viewBox="0 0 210 120" role="presentation" focusable="false">
          <defs>
            <radialGradient id="v2-lantern-glow" cx="50%" cy="50%" r="50%">
              <stop offset="0" stopColor="#ffe9b8" stopOpacity=".9" />
              <stop offset=".55" stopColor="#ffd98e" stopOpacity=".34" />
              <stop offset="1" stopColor="#ffd98e" stopOpacity="0" />
            </radialGradient>
            <linearGradient id="v2-paper-hull" x1="0" x2="1" y1="0" y2="1">
              <stop offset="0" stopColor="#f7efdc" />
              <stop offset="1" stopColor="#e2cfa6" />
            </linearGradient>
            <linearGradient id="v2-paper-sail" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0" stopColor="#fdf6e6" />
              <stop offset="1" stopColor="#f2e3c0" />
            </linearGradient>
          </defs>
          {/* 贴水面暗影 */}
          <ellipse cx="104" cy="113" rx="78" ry="6" fill="rgba(23,42,46,.14)" />
          {/* 折纸船体（尖头朝右） */}
          <path d="M14 88 C 52 106 128 112 192 78 L 186 90 C 132 116 62 116 20 100 Z" fill="url(#v2-paper-hull)" stroke="rgba(40,58,60,.78)" strokeWidth="1.6" strokeLinejoin="round" />
          <path d="M24 97 C 68 108 132 107 178 87" fill="none" stroke="rgba(40,58,60,.3)" strokeWidth=".9" />
          {/* 桅杆 */}
          <path d="M121 84 L 121 24" stroke="rgba(58,44,36,.85)" strokeWidth="2.2" strokeLinecap="round" />
          {/* 米白纸帆 */}
          <path d="M127 30 L 164 76 L 127 76 Z" fill="url(#v2-paper-sail)" stroke="rgba(40,58,60,.7)" strokeWidth="1.4" strokeLinejoin="round" />
          {/* 帆顶朱红小旗 */}
          <path d="M121 24 L 143 31 L 121 38 Z" fill="#b03a2c" stroke="rgba(120,30,22,.55)" strokeWidth=".8" strokeLinejoin="round" />
          {/* 船尾灯 */}
          <circle cx="36" cy="96" r="15" fill="url(#v2-lantern-glow)" />
          <circle cx="36" cy="96" r="2.6" fill="#ffe2a0" />
        </svg>
      </div>
    </div>
  );
}
